/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1751932800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Jul 08 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "kognitiv",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Connect to Kognitiv",
          "depth": 1,
          "id": "connect-to-kognitiv"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "File import specifications",
          "depth": 1,
          "id": "file-import-specifications"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      p: "p",
      h3: "h3",
      a: "a",
      strong: "strong",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Any data set"
            }), _jsx(_components.td, {
              children: "Sync data from a source to a file-import specification in Kognitiv"
            }), _jsx(_components.td, {
              children: "Mirror"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: "Mirror mode means one file with all the rows in the query results will be created, every time the sync runs."
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-kognitiv",
        children: "Connect to Kognitiv"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Kognitiv"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch syncs to Kognitiv via their UploadFile Web Service, which uses a security token. Enter your security token.\nIf you do not have a security token, contact Kognitiv Loyalty Support to set one up."
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.h3, {
        id: "file-import-specifications",
        children: "File import specifications"
      }), "\n", _jsx(_components.p, {
        children: "Choose which file import specification you'd like to import to Kognitiv. Hightouch offers templates for:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Member descriptor file"
        }), "\n", _jsx(_components.li, {
          children: "Loyalty member custom fields"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Access the complete list of Kognitiv's file import options ", _jsx(_components.a, {
          href: "https://info.kognitivloyalty.com/File_Import_Specifications.html",
          children: "here"
        }), ".\nIf you'd like to sync to a file import specification that isn't listed, select ", _jsx(_components.strong, {
          children: "Custom"
        }), " and enter in the column fields as mappings."]
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "If you selected one of the file import specifications listed above, column mapping options will be available to select from. You can add additional mappings by creating a new field."
      }), "\n", _jsxs(_components.p, {
        children: ["If you selected ", _jsx(_components.strong, {
          children: "Custom"
        }), " file import specification, enter the field that you'd like to map data to."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
